import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * SINS — The Forbidden Treasure Indicator
 *
 * A dark sentinel that watches your accumulation of forbidden desires,
 * illuminating the count of temptations held within the cart.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The pulsating orb of temptation.
 * @property {HTMLElement} cartBubbleText - The whispering text within the orb.
 * @property {HTMLElement} cartBubbleCount - The numeric sign of desire's weight.
 *
 * @extends {Component<Refs>}
 */
class SinsCartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentDesireCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentDesireCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onDesireUpdate);
    window.addEventListener('pageshow', this.onPageRevive);
    this.ensureOrbReflectsTruth();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onDesireUpdate);
    window.removeEventListener('pageshow', this.onPageRevive);
  }

  /**
   * Handles page revive events, ensuring the orb stays truthful.
   * @param {PageTransitionEvent} event
   */
  onPageRevive = (event) => {
    if (event.persisted) {
      this.ensureOrbReflectsTruth();
    }
  };

  /**
   * Responds to updates in the collection of desires.
   * @param {CartUpdateEvent} event
   */
  onDesireUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const fromTemptationForm = event.detail.data?.source === 'product-form-component';

    this.renderOrb(itemCount, fromTemptationForm);
  };

  /**
   * Renders the orb reflecting current desires.
   * @param {number} itemCount
   * @param {boolean} fromTemptationForm
   * @param {boolean} [animate=true]
   */
  renderOrb = async (itemCount, fromTemptationForm, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentDesireCount = fromTemptationForm ? this.currentDesireCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentDesireCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Ensures the orb displays the truth after page revival.
   */
  ensureOrbReflectsTruth = () => {
    if (!this.refs.cartBubbleCount) return;

    const stored = sessionStorage.getItem('cart-count');

    if (stored === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(stored);

      if (value === visibleCount) return;

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderOrb(count, false, false);
        }
      }
    } catch {
      // no-op
    }
  };
}

if (!customElements.get('sins-cart-icon')) {
  customElements.define('sins-cart-icon', SinsCartIcon);
}
